import { renderModal, closeModal } from './ui.mjs';
import { createTextInput } from './components.mjs';
import router from './router.mjs';
import { openCurrencyPicker } from './currencyPicker.mjs';
import { appendModalFooterButton } from './uiHelpers.mjs';
import t from 'virtual:i18n';

/**
 * Shows the "Create New Tab" modal bottom sheet
 * Similar to Flutter's CreateEmptyTabWidget
 */
export async function showCreateTabModal() {
    // State
    let tabName = '';
    let currency = 'USD';
    let participants = [''];
    let hasValidData = false;
    let createButton = null;
    let modal = null;

    // Create the modal body
    const body = document.createElement('form');
    body.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 0 4px;
        margin: 0;
        min-height: 400px;
    `;
    // Tab name input
    const tabNameContainer = document.createElement('div');
    tabNameContainer.style.marginBottom = '8px';

    const tabNameLabel = document.createElement('label');
    tabNameLabel.textContent = t.tabName;
    tabNameLabel.style.cssText = `
        display: block;
        font-size: 12px;
        color: #666;
        margin-bottom: 4px;
    `;
    tabNameContainer.appendChild(tabNameLabel);

    const tabNameInput = document.createElement('input');
    tabNameInput.type = 'text';
    tabNameInput.placeholder = t.tabName;
    tabNameInput.setAttribute('autocomplete', 'off');
    tabNameInput.setAttribute('autocorrect', 'off');
    tabNameInput.setAttribute('spellcheck', 'false');
    tabNameInput.setAttribute('data-form-type', 'other');
    tabNameInput.style.cssText = `
        width: 100%;
        padding: 12px 16px;
        font-size: 16px;
        border: 1px solid #ccc;
        border-radius: 20px;
        outline: none;
        box-sizing: border-box;
        transition: border-color 0.2s;
    `;
    tabNameInput.addEventListener('focus', () => {
        tabNameInput.style.borderColor = 'var(--color-primary)';
        // Select all text on focus
        tabNameInput.select();
    });
    tabNameInput.addEventListener('blur', () => {
        tabNameInput.style.borderColor = '#ccc';
    });
    tabNameInput.addEventListener('input', (e) => {
        tabName = e.target.value.trim();
        updateValidation();
    });

    // Handle Enter key to jump to first participant or submit form
    tabNameInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const firstParticipantInput = participantsContainer.isConnected ? participantsContainer.querySelector('input') : null;
            if (firstParticipantInput) {
                // Focus first participant input
                setTimeout(() => {
                    firstParticipantInput.focus();
                }, 100);
            } else {
                executeTabCreation();
            }
        }
    });

    tabNameContainer.appendChild(tabNameInput);
    body.appendChild(tabNameContainer);

    // Currency selector
    const currencyContainer = document.createElement('div');
    currencyContainer.style.marginBottom = '16px';

    const currencyLabel = document.createElement('label');
    currencyLabel.textContent = t.currency;
    currencyLabel.style.cssText = `
        display: block;
        font-size: 12px;
        color: #666;
        margin-bottom: 4px;
    `;
    currencyContainer.appendChild(currencyLabel);

    const currencyBtn = document.createElement('div');
    currencyBtn.className = 'currency-select-btn';
    currencyBtn.style.cssText = `
        width: 100%;
        box-sizing: border-box;
        padding: 12px 16px;
        font-size: 16px;
        border-radius: 20px;
        justify-content: space-between;
    `;
    currencyBtn.title = 'Tap to choose currency';
    currencyBtn.innerHTML = `
        <span class="currency-btn-code">${currency}</span>
        <span class="currency-chevron">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
        </span>
    `;
    currencyBtn.addEventListener('click', () => {
        openCurrencyPicker(currency, (selectedCode) => {
            currency = selectedCode;
            currencyBtn.querySelector('.currency-btn-code').textContent = currency;
        });
    });

    currencyContainer.appendChild(currencyBtn);
    body.appendChild(currencyContainer);

    // Participants section
    const participantsHeader = document.createElement('div');
    participantsHeader.textContent = t.addGroupMembersBelow;
    participantsHeader.style.cssText = `
        text-align: center;
        color: #666;
        font-size: 14px;
        margin-top: 16px;
        margin-bottom: 16px;
    `;
    //body.appendChild(participantsHeader);

    // Participants list container
    const participantsContainer = document.createElement('div');
    participantsContainer.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 8px;
        margin-bottom: 96px;
    `;
    //body.appendChild(participantsContainer);

    // Helper function to get ordinal suffix
    function getOrdinalSuffix(n) {
        if (n > 3 && n < 21) return 'th';
        switch (n % 10) {
            case 1: return 'st';
            case 2: return 'nd';
            case 3: return 'rd';
            default: return 'th';
        }
    }

    // Function to create participant input
    function createParticipantInput(index) {
        const container = document.createElement('div');
        container.style.cssText = `
            display: flex;
            align-items: center;
            gap: 8px;
        `;

        const input = document.createElement('input');
        input.type = 'text';

        let personStr = t.person || '{value1}{value2} Person';
        personStr = personStr.replace('{value1}', index + 1).replace('{value2}', getOrdinalSuffix(index + 1));
        input.placeholder = index === 0 ? t.yourName : personStr;

        input.setAttribute('autocomplete', 'nope');
        input.setAttribute('autocorrect', 'off');
        input.setAttribute('spellcheck', 'false');
        input.setAttribute('data-form-type', 'other');
        input.value = participants[index] || '';
        input.style.cssText = `
            flex: 1;
            padding: 12px 16px;
            font-size: 16px;
            border: 1px solid #ccc;
            border-radius: 20px;
            outline: none;
            box-sizing: border-box;
            transition: border-color 0.2s;
        `;

        input.addEventListener('focus', () => {
            input.style.borderColor = 'var(--color-primary)';
            // Select all text on focus
            input.select();
        });

        input.addEventListener('blur', () => {
            input.style.borderColor = '#ccc';
        });

        input.addEventListener('input', (e) => {
            participants[index] = e.target.value.trim();

            // If this is the last field and it's not empty, add a new field
            if (index === participants.length - 1 && e.target.value.trim()) {
                participants.push('');
                renderParticipants(index); // Pass the current index to maintain focus
            }

            updateValidation();
        });

        // Handle Enter key to move to next field, scroll to bottom, or submit form
        input.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();

                // If this is the last field and has content, add a new field
                if (index === participants.length - 1 && input.value.trim()) {
                    participants.push('');
                    renderParticipants(index + 1); // Focus the new field
                } else if (index < participants.length - 1) {
                    // Focus the next existing field
                    const nextInput = participantsContainer.children[index + 1]?.querySelector('input');
                    if (nextInput) {
                        nextInput.focus();
                    }
                } else {
                    // Last field and it is empty, so submit/create the tab
                    executeTabCreation();
                }

                // Always scroll to bottom on Enter
                setTimeout(() => {
                    const scrollBody = document.querySelector('.sheet-scroll-body');
                    if (scrollBody) {
                        scrollBody.scrollTo({
                            top: scrollBody.scrollHeight,
                            behavior: 'smooth'
                        });
                    }
                }, 100);
            }
        });

        container.appendChild(input);

        // Add remove button for all except first participant
        if (index > 0) {
            const removeBtn = document.createElement('button');
            removeBtn.type = 'button';
            removeBtn.innerHTML = '×';
            removeBtn.style.cssText = `
                width: 40px;
                height: 40px;
                border-radius: 50%;
                border: 1px solid #ccc;
                background-color: white;
                color: #666;
                font-size: 24px;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: center;
                transition: all 0.2s;
            `;
            removeBtn.addEventListener('mouseenter', () => {
                removeBtn.style.backgroundColor = '#f5f5f5';
                removeBtn.style.borderColor = '#999';
            });
            removeBtn.addEventListener('mouseleave', () => {
                removeBtn.style.backgroundColor = 'white';
                removeBtn.style.borderColor = '#ccc';
            });
            removeBtn.addEventListener('click', () => {
                participants.splice(index, 1);
                // Ensure at least 2 fields
                if (participants.length < 2) {
                    participants.push('');
                }
                renderParticipants();
                updateValidation();
            });
            container.appendChild(removeBtn);
        }

        return { container, input };
    }

    // Function to render all participant inputs
    function renderParticipants(focusIndex = null) {
        participantsContainer.innerHTML = '';
        participants.forEach((_, index) => {
            const { container, input } = createParticipantInput(index);
            participantsContainer.appendChild(container);

            // Re-focus the input if this is the one that was focused
            if (focusIndex === index) {
                // Use setTimeout to ensure the DOM is updated
                setTimeout(() => {
                    input.focus();
                    // Move cursor to end of input
                    input.setSelectionRange(input.value.length, input.value.length);
                }, 0);
            }
        });
    }

    // Function to update validation and button state
    function updateValidation() {
        // Only require tab name, participants are optional
        hasValidData = tabName.length > 0;

        if (createButton) {
            createButton.disabled = !hasValidData;
            createButton.style.opacity = hasValidData ? '1' : '0.5';
            createButton.style.cursor = hasValidData ? 'pointer' : 'not-allowed';
        }
    }

    // Initialize with 2 empty participants
    participants = ['', ''];
    renderParticipants();

    // Define shared tab creation logic (hoisted automatically)
    async function executeTabCreation() {
        if (!hasValidData) return;

        const validParticipants = participants.filter(p => p.trim().length > 0);

        try {
            const { createGroupFromWeb, addParticipant } = await import('./api.mjs');
            const newGroup = await createGroupFromWeb(tabName, currency);

            // Add participants sequentially in the background to respect OCC versioning
            if (validParticipants.length > 0) {
                (async () => {
                    for (const participant of validParticipants) {
                        try {
                            await addParticipant(newGroup.id, participant);
                        } catch (e) {
                            console.error(`Failed to add participant ${participant} in background:`, e);
                        }
                    }
                })();
            }

            const { trackEvent } = await import('./utils.mjs');
            trackEvent('group_created', { currency: currency, participant_count: validParticipants.length });
            if (modal) {
                closeModal(modal);
            }
            router.navigate(`/home/${newGroup.id}`);
            if (window.location.pathname === '/' || window.location.pathname === '/home') {
                const { renderHome } = await import('./renderHome.mjs');
                renderHome();
            }
        } catch (error) {
            console.error('Failed to create group:', error);
            alert(`Failed to create tab: ${error.message}`);
        }
    }

    // Handle form submission (mobile keyboard onSubmit / Go button)
    body.addEventListener('submit', async (e) => {
        e.preventDefault();
        await executeTabCreation();
    });

    // Create the modal
    modal = renderModal({
        anchorEl: document.body,
        title: t.createTab,
        body: body,
        buttons: [],
        onClose: () => {
            // Cleanup if needed
        }
    });

    createButton = document.createElement('button');
    createButton.className = 'header-action-text-btn';
    createButton.innerHTML = `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg><span>${t.create}</span>`;

    createButton.onclick = async (e) => {
        e.stopPropagation();
        await executeTabCreation();
    };

    const headerSection = modal.querySelector('.sheet-header-section');
    if (headerSection) {
        headerSection.appendChild(createButton);
    }

    // Initial validation check
    updateValidation();


    // Focus on tab name input after a short delay (desktop only)
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (!isMobile) {
        setTimeout(() => {
            tabNameInput.focus();
        }, 350);
    }

    return modal;
}
